import {useEffect, useState} from "react";
import {MailDataType, RenderTextBlockPreviewQuery, useRenderTextBlockPreviewQuery} from "../../../generated/graphql";
import {ResolvedTextBlocks} from "../MjmlLoader/processTemplateData";

/** Empfängertypen mit Vorschau (Plan 2026-09-02 Q3): Mitarbeiter bleibt roh, Rechnung/Anwesenheitsliste außen vor. */
export const PREVIEW_MAIL_DATA_TYPES: Array<MailDataType> = [MailDataType.Booker, MailDataType.Attendee, MailDataType.Booking, MailDataType.Course, MailDataType.ScheduledPayment];

export type TextBlockDraft = {
    subject?: string | null;
    headerText?: string | null;
    footerText?: string | null;
    signature?: string | null;
};

export type TextBlockPreviewState = {
    /** false: keine Empfänger-IDs oder Typ ohne Vorschau → Hinweis „Variablen werden beim Versand ersetzt". */
    possible: boolean;
    loading: boolean;
    error?: Error;
    recipients: RenderTextBlockPreviewQuery["renderTextBlockPreview"]["recipients"];
    current?: NonNullable<RenderTextBlockPreviewQuery["renderTextBlockPreview"]["current"]>;
    /** Legende über alle Empfänger; fehlt bei einem Empfänger, bei Musterdaten und über 200 Empfängern. */
    summary?: NonNullable<RenderTextBlockPreviewQuery["renderTextBlockPreview"]["summary"]>;
    /** true: Vorlagen-Seite ohne Empfänger, die Leiste zeigt „Musterdaten" statt Name/E-Mail. */
    sample: boolean;
    resolvedTextBlocks?: ResolvedTextBlocks;
    index: number;
    setIndex: (index: number) => void;
    /** Variablennamen als Etikett hinter jedem ersetzten Wert (Schalter in der Leiste). */
    showVariableNames: boolean;
    setShowVariableNames: (show: boolean) => void;
};

type Options = {
    /** Nur im Vorschau-Modus laden; beim Zurückschalten aus dem Editor rendert der aktuelle Entwurf. */
    enabled: boolean;
    templateId?: string;
    mailDataType?: MailDataType;
    ids: Array<string>;
    draft: TextBlockDraft;
    notifyBookings?: boolean;
    notifyWaitlist?: boolean;
    notifyCancelations?: boolean;
    /** Ohne Empfänger-IDs (Vorlagen-Seite) einen synthetischen Beispiel-Empfänger rendern. */
    sampleData?: boolean;
};

export const useTextBlockPreview = ({enabled, templateId, mailDataType, ids, draft, notifyBookings, notifyWaitlist, notifyCancelations, sampleData}: Options): TextBlockPreviewState => {
    const [index, setIndex] = useState(0);
    const [showVariableNames, setShowVariableNames] = useState(false);
    const idsKey = ids.join(",");

    useEffect(() => {
        setIndex(0);
    }, [idsKey, mailDataType]);

    const sample = Boolean(sampleData) && ids.length === 0;
    const possible = Boolean(templateId) && (ids.length > 0 || sample) && mailDataType !== undefined && PREVIEW_MAIL_DATA_TYPES.includes(mailDataType);

    const input = {
        templateId: templateId ?? "",
        mailDataType: mailDataType ?? MailDataType.Booker,
        ids,
        editedTemplateInput: {
            subject: draft.subject ?? undefined,
            headerText: draft.headerText ?? undefined,
            footerText: draft.footerText ?? undefined,
            signature: draft.signature ?? undefined,
        },
        notifyBookings,
        notifyWaitlist,
        notifyCancelations,
        sampleData: sample,
    };

    const {data, previousData, loading, error} = useRenderTextBlockPreviewQuery({
        skip: !enabled || !possible,
        variables: {input: {...input, recipientIndex: index}},
    });

    // Beim Blättern bleibt der vorige Empfänger stehen, bis der neue geladen ist (kein Rohtext-Flackern).
    const result = (data ?? previousData)?.renderTextBlockPreview;
    const current = result?.current ?? undefined;
    const recipients = result?.recipients ?? [];

    // Legende über alle Empfänger: eigene Abfrage ohne recipientIndex, damit sie je Entwurf
    // einmal läuft und beim Blättern aus dem Apollo-Cache kommt. `skip` hängt am Ergebnis der
    // ersten Abfrage; das schleift nicht, weil Apollo die Variablen tief vergleicht und bei
    // gleichem Input nicht erneut lädt.
    const {data: summaryData} = useRenderTextBlockPreviewQuery({
        skip: !enabled || !possible || sample || recipients.length < 2,
        variables: {input: {...input, summary: true}},
    });

    return {
        possible,
        loading,
        error,
        recipients,
        current,
        summary: summaryData?.renderTextBlockPreview.summary ?? undefined,
        sample,
        resolvedTextBlocks: current ? {subject: current.subject, headerText: current.headerText, footerText: current.footerText, signature: current.signature} : undefined,
        index: current?.index ?? index,
        setIndex,
        showVariableNames,
        setShowVariableNames,
    };
};
